import { memo, type FC } from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import type { PreviewListResponse } from "src/layouts/Engagements/EngagementInterfaces";
import { readFormattedText } from "src/layouts/Engagements/Overview/Mappers/OverviewMappers";
import type { ListCleanup } from "../createListCsv";
import { useCreateListLabel } from "../useCreateListLabel";

interface ReviewStepProps {
  listName: string;
  listMeta: string;
  preview: PreviewListResponse;
  cleanup: ListCleanup;
  onDownloadSkipped: () => void;
  onDownloadList: () => void;
}

const ReviewStep: FC<ReviewStepProps> = ({
  listName,
  listMeta,
  preview,
  cleanup,
  onDownloadSkipped,
  onDownloadList,
}) => {
  const { i18n } = useTranslation();
  const label = useCreateListLabel();
  const formatValue = (value: number) => formatNumber(value, {}, i18n.language);
  const readPreviewValue = (value: number, formatted?: string | null) =>
    readFormattedText(formatted) ?? formatValue(value ?? 0);
  const existingCount = preview.already_in_datatram ?? 0;
  const existingLabel = readPreviewValue(existingCount, preview.already_in_datatramFormattedValue);
  const skippedCount = cleanup.skippedRows.length;

  return (
    <CampaignStepCard title={label("review.title")}>
      <div className="create-list__stats">
        <div className="create-list__stat">
          <strong className="create-list__stat-value">
            {readPreviewValue(preview.total_people, preview.total_peopleFormattedValue)}
          </strong>
          <span className="create-list__stat-label">{label("review.peopleInList")}</span>
        </div>
        <div className="create-list__stat">
          <strong className="create-list__stat-value create-list__stat-value--positive">
            {readPreviewValue(preview.new_to_datatram, preview.new_to_datatramFormattedValue)}
          </strong>
          <span className="create-list__stat-label">{label("review.newToDatatram")}</span>
        </div>
        <div className="create-list__stat">
          <strong className="create-list__stat-value">{existingLabel}</strong>
          <span className="create-list__stat-label">{label("review.alreadyInDatatram")}</span>
        </div>
      </div>

      <div className="create-list__review-notes">
        {cleanup.duplicateCount > 0 && (
          <p>
            <strong>
              {label("review.duplicates", {
                count: cleanup.duplicateCount,
                value: formatValue(cleanup.duplicateCount),
              })}
            </strong>{" "}
            {label("review.duplicatesDetail")}
          </p>
        )}
        {skippedCount > 0 && (
          <p>
            <strong className="create-list__text-warning">
              {label("review.skipped", { count: skippedCount, value: formatValue(skippedCount) })}
            </strong>{" "}
            {label("review.skippedDetail")}{" "}
            <button type="button" className="create-list__link" onClick={onDownloadSkipped}>
              {label("review.downloadSkipped")}
            </button>
          </p>
        )}
        {existingCount > 0 && (
          <p>{label("review.existingDetail", { value: existingLabel })}</p>
        )}
      </div>

      <div className="create-list__summary">
        <span className="create-list__summary-text" dir="auto">
          <strong>{listName}</strong> · {listMeta}
        </span>
        <button
          type="button"
          className="create-list__icon-btn"
          aria-label={label("review.downloadList")}
          onClick={onDownloadList}
        >
          <Download size={18} />
        </button>
      </div>
    </CampaignStepCard>
  );
};

export default memo(ReviewStep);
